<template>
  <div class="contents koyoukeiyaku">
    <h2>雇用契約 新規作成</h2>
    <div class="container edit-page" :class="'mode-' + mode">
      <loading v-if="!isInit && !isError" :message="'初期化中...'"></loading>
      <p v-else-if="isError">画面の初期化に失敗しました</p>
      <p v-else-if="
        mode === OptionConst.KOYOUKEIYAKU_EDIT_MODE.FROM &&
        !keiyakuDetail
      ">作成元となるデータが存在しません。</p>
      <div class="select-sheet" v-else>

        <!-- TODO: 作業確認用（後で消す） -->
        <!-- <p class="link-text" @click="showDebugData = !showDebugData">[開発用] editingData<span
            v-if="showDebugData">非</span>表示</p>
        <p v-if="showDebugData">{{ editingKoyouKeiyaku }}</p> -->
        <!-- ここまで -->

        <div class="options" v-if="!isEditing">
          <h3>雇用契約の種別等を選択してください。</h3>
          <div v-if="canSelectSyozoku" class="row">
            <div class="edit-item">
              <div class="head"><b>所属</b></div>
              <div class="data">
                <select-box :options="options?.syozoku" v-model="editingKoyouKeiyaku.syozoku_id"
                  @update:modelValue="updateSyozoku"></select-box>
              </div>
            </div>
          </div>

          <div class="row">
            <div class="edit-item">
              <div class="head"><b>申請種別</b></div>

              <div class="data">
                <!-- 新規作成の場合は「新規」のみ選択可能 -->
                <input-radio v-if="shinseiTypeOptions" :options="shinseiTypeOptions"
                  v-model="editingKoyouKeiyaku.shinsei_type"></input-radio>
                <p v-else>選択可能な申請種別がありません</p>
              </div>
            </div>
          </div>

          <div class="row">
            <div class="edit-item">
              <div class="head"><b>雇用形態種別</b></div>
              <div class="data">
                <input-radio v-if="koyouTypeOptions" :options="koyouTypeOptions"
                  v-model="editingKoyouKeiyaku.koyou_type"></input-radio>
                <p v-else>選択可能な雇用形態種別がありません</p>
              </div>
            </div>
          </div>

          <!-- 形態種別 -->
          <div class="row">
            <div class="edit-item">
              <div class="head"><b>形態種別</b></div>
              <div class="data">
                <input-radio v-if="keitaiTypeOptions" :options="keitaiTypeOptions"
                  v-model="editingKoyouKeiyaku.keitai_type"></input-radio>
                <p v-else>選択可能な形態種別がありません</p>
              </div>
            </div>
          </div>

          <div class="row">
            <div class="edit-item">
              <div class="head"><b>雇用期間の定め</b></div>
              <div class="data">
                <template
                  v-if="editingKoyouKeiyaku.koyou_type === OptionConst.KOYOU_TYPE.GEKKYUU_MUKI || editingKoyouKeiyaku.koyou_type === OptionConst.KOYOU_TYPE.PART_MUKI">
                  <p class="selected">無期</p>
                </template>
                <template v-else>
                  <p class="selected">有期</p>
                </template>
              </div>
            </div>
          </div>

        </div><!-- options -->

        <div class="buttons">
          <button class="btn primary" v-on:click="changeKoyouType()" v-if="isEditing">&lt;&lt; 種別等を選び直す</button>
          <button class="btn primary" v-on:click="changeFormTemplate()" v-if="!isEditing" :disabled="!canEdit">契約情報の入力に進む
            &gt;&gt;</button>
        </div>
      </div><!-- select-sheet -->

      <div class="edit-sheet" v-if="isEditing">
        <h3><i class="fa-regular fa-file-lines"></i>{{ sheetTitle }}</h3>
        <edit-form :editingData="editingKoyouKeiyaku" :currentData="keiyakuDetail" :mode="mode"
          @backTypeSelect="isEditing = false" />
      </div>

    </div><!--container-->

    <div class="button-container">
      <button type="button" class="btn danger" @click="exit()" :disabled="isLoading || isSaving">
        保存せずに終了
      </button>

      <button type="button" class="btn success" @click="createContract()" :disabled="isLoading || isSaving"
        v-if="isInit && isEditing">
        <template v-if="isSaving">
          <i class="fa-solid fa-spinner fa-spin-pulse"></i>作成中…
        </template>
        <template v-else>
          <i class="fa-solid fa-file-lines"></i>雇用契約書を保存する
        </template>
      </button>
    </div><!--button-container-->

  </div><!--contents-->
</template>

<script lang="ts">

import { defineComponent, ref, onMounted, Ref, computed, toRefs } from 'vue'
import { useKoyouKeiyakuInit } from '@/composable/koyoukeiyaku/init';
import { useKoyouKeiyakuEdit } from '@/composable/koyoukeiyaku/edit';
import OptionConst from '@/const/Option';
import { RouterConst } from '@/const/Router';
import { useRoute, useRouter } from 'vue-router';
import { useKoyouKeiyakuDetail } from '@/composable/koyoukeiyaku/detail';
import { cloneDeep, method } from 'lodash';
import EditForm from '@/components/koyoukeiyaku/EditForm.vue';
import { DialogService } from '@/service/DialogService';
import KoyouKeitaiTypeEdit from '@/components/koyoukeiyaku/parts/KoyouKeitaiTypeEdit.vue'

export default defineComponent({
  async beforeRouteLeave(to, from, next) {
    const result = await this.confirmLeave()
    if (result) {
      next()
      return
    } else {
      next(false)
      return
    }
  },
  setup() {
    const { isLoading, isError, options, shinseiTypeOptionsForCreate, getAllKoyouKeiyakuOption, setKoyouKeiyakuTemplate, sheetTitle } = useKoyouKeiyakuInit()
    const { editingKoyouKeiyaku, upsert, isSaving, canSelectSyozoku, checkCanSelectSyozoku, initForCreate, initForCreateFrom, initBySheetType } = useKoyouKeiyakuEdit()
    const { keiyakuDetail, getKeiyakuDetail } = useKoyouKeiyakuDetail()

    // 離脱確認
    const confirmLeave = async () => {
      const result = await DialogService.question({
        html: '編集を終了しますか？保存されていない内容は破棄されます',
        confirmButtonText: '編集を続ける',
        cancelButtonText: 'ページを離れる',
        showCancelButton: true
      })

      if (result.isConfirmed) {
        return false
      } else {
        return true
      }
    }

    const prevPage = ref({
      name: RouterConst.KOYOUKEIYAKU_MENU,
      params: {}
    })
    const prevPageLabel = ref('雇用契約メニューに戻る')

    // 作成モードと雇用契約IDを取得（新規作成 / 編集）
    const router = useRouter()
    const route = useRoute();
    const mode = route.query.mode ? route.query.mode.toString() : '';
    const keiyakuId = parseInt(route.query.id as string, 10);

    // 雇用契約書作成中
    const isEditing = ref(false)

    // 描画のための初期化済フラグ
    const isInit = ref(false)

    // TODO: デバッグ用
    const showDebugData = ref(false)


    onMounted(async () => {
      if (!mode) {
        await DialogService.error({
          text: '編集モードを取得できません。メニューに戻ります'
        }).then(() => {
          router.push({ name: RouterConst.KOYOUKEIYAKU_MENU })
        })
      }
      await getAllKoyouKeiyakuOption()
      await checkCanSelectSyozoku();
      if (
        mode === OptionConst.KOYOUKEIYAKU_EDIT_MODE.FROM ||
        mode === OptionConst.KOYOUKEIYAKU_EDIT_MODE.EDIT
      ) {
        prevPage.value = {
          name: RouterConst.KOYOUKEIYAKU_DETAIL,
          params: {
            id: keiyakuId
          }
        }
        prevPageLabel.value = '詳細画面に戻る'

        // ある雇用契約をもとにして作成する場合
        await getKeiyakuDetail(keiyakuId)
        if (keiyakuDetail.value) {
          editingKoyouKeiyaku.value = cloneDeep(keiyakuDetail.value);
        } else {
          DialogService.error({
            text: '雇用契約の取得に失敗しました。前の画面へ戻ります'
          })
          // TODO: 直アクセスの場合を考慮する
          router.go(-1)
        }

        if (mode === OptionConst.KOYOUKEIYAKU_EDIT_MODE.FROM) {
          // 元にする契約情報から不要な情報、削除すべき情報などを処理する
          await initForCreateFrom();
        } else if (mode === OptionConst.KOYOUKEIYAKU_EDIT_MODE.EDIT) {
          // 各種別の選択をスキップさせる
          await setKoyouKeiyakuTemplate(editingKoyouKeiyaku.value);
          isEditing.value = true
        }
      } else {
        // 新規作成の場合
        await initForCreate();
        // await prepareBySheetType(sheetType.value)
      }
      isInit.value = true
    })

    // ボタン：契約情報の入力に進む
    const changeFormTemplate = async () => {
      await setKoyouKeiyakuTemplate(editingKoyouKeiyaku.value);
      isEditing.value = true

      // シートタイプによるデータ整形処理
      if (mode === OptionConst.KOYOUKEIYAKU_EDIT_MODE.CREATE) {
        await initBySheetType(mode);
      } else if (
        mode === OptionConst.KOYOUKEIYAKU_EDIT_MODE.FROM ||
        mode === OptionConst.KOYOUKEIYAKU_EDIT_MODE.EDIT
      ) {
        await initBySheetType(mode, keiyakuDetail.value);
      }
    }

    // ボタン：雇用形態種別を変更する
    const changeKoyouType = async () => {
      isEditing.value = false;
    }

    // ボタン：雇用契約書を保存する 
    const createContract = () => {
      if (!editingKoyouKeiyaku.value) {
        return;
      }
      else {
        upsert(editingKoyouKeiyaku.value);
      }
    }

    // 入社タイプで、就業場所を選択後に、所属会社を変更した場合の処理
    // (その所属会社に存在しない就業場所IDが保持されるのを防ぐため、就業場所IDをnullにする)
    const updateSyozoku = () => {
      if (editingKoyouKeiyaku.value) {
        editingKoyouKeiyaku.value.mst_syuugyou_basyo_id = null;
      }
    }

    const canEdit = computed(() => {
      if (!editingKoyouKeiyaku.value.syozoku_id) {
        return false
      }
      if (!editingKoyouKeiyaku.value.shinsei_type) {
        return false
      }
      if (!editingKoyouKeiyaku.value.koyou_type) {
        return false
      }
      return true
    })

    // 申請種別
    const shinseiTypeOptions = computed(() => {
      if (!options.value) {
        return []
      }
      if (mode === OptionConst.KOYOUKEIYAKU_EDIT_MODE.CREATE) {
        editingKoyouKeiyaku.value.shinsei_type = shinseiTypeOptionsForCreate.value[0].val
        return shinseiTypeOptionsForCreate.value
      }

      // 所属(プログレス)によるフィルター（TODO: 外販対応ができなくなるので無くしたい）
      let retOptions = options.value.shinsei_type
      if (editingKoyouKeiyaku.value.syozoku_id === OptionConst.SYOZOKU.PG) {
        retOptions = retOptions.filter(opt =>
          opt.val === OptionConst.KOYOU_SHINSEI_TYPE.ENTRY ||
          opt.val === OptionConst.KOYOU_SHINSEI_TYPE.UPDATE ||
          opt.val === OptionConst.KOYOU_SHINSEI_TYPE.ADD ||
          opt.val === OptionConst.KOYOU_SHINSEI_TYPE.CHANGE
        )
      }

      // フィルターされた値を選んでいた場合、選択可能な値の先頭を選択状態にする
      const find = retOptions.find((opt) => editingKoyouKeiyaku.value.shinsei_type === opt.val)
      if (!find) {
        editingKoyouKeiyaku.value.shinsei_type = retOptions[0].val
      }
      return retOptions
    })

    // 雇用形態種別
    const koyouTypeOptions = computed(() => {
      if (!options.value) {
        return []
      }

      
      // MEDICA_AS-1238／MEDICA_AS-1264に関して田中さんと相談した結果、制限を無くすということになったの以下コメントアウト
      return options.value.koyou_type

      // let retOptions = options.value.koyou_type
      // 
      // // 形態種別によるフィルター
      // // ここを有効にするとパートが表示されなくなるため形態種別によるフィルターが不要ならこのまま消す
      // // ※形態種別によるフィルターが必要な場合、既存契約からの作成時の形態種別の初期値を消す必要があるためそれで良いかなどの検討が必要
      // // switch (editingKoyouKeiyaku.value.keitai_type) {
      // //   case OptionConst.KOYOU_KEITAI_TYPE.KEIYAKUSYAIN_PART:
      // //   case OptionConst.KOYOU_KEITAI_TYPE.KEIYAKUSYAIN_PART:
      // //     // パートと時給のみを取得
      // //     retOptions = retOptions.filter(opt =>
      // //       opt.val === OptionConst.KOYOU_TYPE.PART ||
      // //       opt.val === OptionConst.KOYOU_TYPE.PART_MUKI ||
      // //       opt.val === OptionConst.KOYOU_TYPE.JIKYUU
      // //     )
      // //     break;
      // //   case OptionConst.KOYOU_KEITAI_TYPE.KEIYAKUSYAIN_FULLTIME:
      // //   case OptionConst.KOYOU_KEITAI_TYPE.HAKENSYAIN_FULLTIME:
      // //     // パートと時給のみを取得
      // //     retOptions = retOptions.filter(opt =>
      // //       opt.val === OptionConst.KOYOU_TYPE.GEKKYUU ||
      // //       opt.val === OptionConst.KOYOU_TYPE.GEKKYUU_MUKI
      // //     )
      // //     break;
      // //   case OptionConst.KOYOU_KEITAI_TYPE.SEISYAIN:
      // //     // パートと時給のみを取得
      // //     retOptions = retOptions.filter(opt =>
      // //       opt.val === OptionConst.KOYOU_TYPE.GEKKYUU ||
      // //       opt.val === OptionConst.KOYOU_TYPE.GEKKYUU_MUKI ||
      // //       opt.val === OptionConst.KOYOU_TYPE.JIKYUU
      // //     )
      // //     break;
      // //   case OptionConst.KOYOU_KEITAI_TYPE.KEIYAKUSYAIN_PART:
      // //   case OptionConst.KOYOU_KEITAI_TYPE.HAKENSYAIN_PART:
      // //     // パートと時給のみを取得
      // //     retOptions = retOptions.filter(opt =>
      // //       opt.val === OptionConst.KOYOU_TYPE.JIKYUU ||
      // //       opt.val === OptionConst.KOYOU_TYPE.PART ||
      // //       opt.val === OptionConst.KOYOU_TYPE.PART_MUKI
      // //     )
      // //     break;
      // //   default:
      // //     break;
      // // }

      // // 所属(プログレス)によるフィルター（TODO: 外販対応ができなくなるので無くしたい）
      // if (editingKoyouKeiyaku.value.syozoku_id === OptionConst.SYOZOKU.PG) {
      //   switch (editingKoyouKeiyaku.value.shinsei_type) {
      //     case OptionConst.KOYOU_SHINSEI_TYPE.UPDATE:
      //     case OptionConst.KOYOU_SHINSEI_TYPE.CHANGE:
      //       retOptions = retOptions.filter(opt =>
      //         opt.val === OptionConst.KOYOU_TYPE.GEKKYUU ||
      //         opt.val === OptionConst.KOYOU_TYPE.PART
      //       )
      //       break;
      //   }
      // }

      // // 申請種別によるフィルター
      // switch (editingKoyouKeiyaku.value.shinsei_type) {
      //   case OptionConst.KOYOU_SHINSEI_TYPE.UPDATE:
      //     // 更新の場合はパート無期は作成不可
      //     retOptions = retOptions.filter(opt =>
      //       opt.val !== OptionConst.KOYOU_TYPE.PART_MUKI
      //     )
      //     break
      //   case OptionConst.KOYOU_SHINSEI_TYPE.CHANGE:
      //     // 内容変更の場合は時給は作成不可
      //     retOptions = retOptions.filter(opt =>
      //       opt.val !== OptionConst.KOYOU_TYPE.JIKYUU
      //     )
      //     break
      // }
      // // フィルターされた値を選んでいた場合、選択可能な値の先頭を選択状態にする
      // const find = retOptions.find((opt) => editingKoyouKeiyaku.value.koyou_type === opt.val)
      // if (!find) {
      //   editingKoyouKeiyaku.value.koyou_type = retOptions[0].val
      // }

      // return retOptions
    })

    // 形態種別
    const keitaiTypeOptions = computed(() => {
      if (!options.value) {
        return []
      }

      // MEDICA_AS-1238／MEDICA_AS-1264に関して田中さんと相談した結果、制限を無くすということになったの以下コメントアウト
      return options.value.keitai_type
      // let retOptions = options.value.keitai_type

      // switch (editingKoyouKeiyaku.value.koyou_type) {
      //   case OptionConst.KOYOU_TYPE.GEKKYUU:
      //   case OptionConst.KOYOU_TYPE.GEKKYUU_MUKI:
      //     retOptions = retOptions.filter(opt =>
      //       opt.val === OptionConst.KOYOU_KEITAI_TYPE.SEISYAIN ||
      //       opt.val === OptionConst.KOYOU_KEITAI_TYPE.KEIYAKUSYAIN_FULLTIME ||
      //       opt.val === OptionConst.KOYOU_KEITAI_TYPE.HAKENSYAIN_FULLTIME
      //     )
      //     break
      //   case OptionConst.KOYOU_TYPE.PART:
      //   case OptionConst.KOYOU_TYPE.PART_MUKI:
      //     retOptions = retOptions.filter(opt =>
      //       opt.val === OptionConst.KOYOU_KEITAI_TYPE.KEIYAKUSYAIN_PART ||
      //       opt.val === OptionConst.KOYOU_KEITAI_TYPE.HAKENSYAIN_PART
      //     )
      //     break
      //   case OptionConst.KOYOU_TYPE.JIKYUU:
      //     retOptions = retOptions.filter(opt =>
      //       opt.val === OptionConst.KOYOU_KEITAI_TYPE.SEISYAIN ||
      //       opt.val === OptionConst.KOYOU_KEITAI_TYPE.KEIYAKUSYAIN_PART ||
      //       opt.val === OptionConst.KOYOU_KEITAI_TYPE.HAKENSYAIN_PART
      //     )
      //     break
      // }

      // // フィルターされた値を選んでいた場合、選択可能な値の先頭を選択状態にする
      // const find = retOptions.find((opt) => editingKoyouKeiyaku.value.keitai_type === opt.val)
      // if (!find) {
      //   editingKoyouKeiyaku.value.keitai_type = retOptions[0].val
      // }

      // return retOptions
    })

    const exit = async () => {
      if (await confirmLeave()) {
        router.push(prevPage.value)
      }
    }

    return {
      showDebugData,
      mode,
      isInit,
      isLoading,
      isError,
      isEditing,
      canEdit,
      changeFormTemplate,
      options,
      editingKoyouKeiyaku,
      sheetTitle,
      createContract,
      isSaving,
      RouterConst,
      changeKoyouType,
      canSelectSyozoku,
      OptionConst,
      updateSyozoku,
      shinseiTypeOptionsForCreate,
      keiyakuId,
      keiyakuDetail,
      prevPage,
      prevPageLabel,
      koyouTypeOptions,
      shinseiTypeOptions,
      keitaiTypeOptions,
      exit,
      confirmLeave
    }
  },
  components: {
    'edit-form': EditForm,  // 共通テンプレート
    'koyou-keitai-type-edit': KoyouKeitaiTypeEdit,
  }
})

</script>

<style lang="scss" scoped>
@import '@scss/components/koyoukeiyaku.scss';
@import '@scss/components/koyoukeiyaku-edit.scss';
</style>
